import type { SlotProps } from "@radix-ui/react-slot";
import type { RegionPolygonFragment } from "../client/generated";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "@acdc2/ui/lib/utils";
import { getRegionType } from "../types/utils";
import { useOverlayScale } from "../editor/hooks";

type Props = SlotProps & {
  fragment: RegionPolygonFragment;
};

export function RegionPolygon({ fragment, className, ...props }: Props) {
  const overlayScale = useOverlayScale();
  const regionType = getRegionType(fragment);

  return (
    <Slot {...props}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">
        <Slottable>
          <polygon
            tabIndex={fragment.readingOrderIndex + 1}
            points={fragment.shape.vertices
              .map((vertex) => `${vertex.left * 100},${vertex.top * 100}`)
              .join(" ")}
            strokeWidth={2 / overlayScale}
            vectorEffect="non-scaling-stroke"
            className={cn(
              "outline-hidden fill-transparent mix-blend-multiply pointer-events-auto",
              {
                "animate-untyped-attribute focus:fill-region-untyped-mid/50 hover:fill-region-untyped-mid/50":
                  regionType === "untyped",
                "animate-hybrid-attribute focus:fill-region-hybrid-mid/50 hover:fill-region-hybrid-mid/50":
                  regionType === "hybrid",
                "animate-heading-attribute focus:fill-region-heading-mid/50 hover:fill-region-heading-mid/50":
                  regionType === "heading",
                "animate-caption-attribute focus:fill-region-caption-mid/50 hover:fill-region-caption-mid/50":
                  regionType === "caption",
                "animate-formula-attribute focus:fill-region-formula-mid/50 hover:fill-region-formula-mid/50":
                  regionType === "formula",
                "animate-figure-attribute focus:fill-region-figure-mid/50 hover:fill-region-figure-mid/50":
                  regionType === "figure",
                "animate-image-attribute focus:fill-region-image-mid/50 hover:fill-region-image-mid/50":
                  regionType === "image",
                "animate-list-attribute focus:fill-region-list-mid/50 hover:fill-region-list-mid/50":
                  regionType === "list" ||
                  regionType === "listItem" ||
                  regionType === "sublist",
                "animate-artifact-attribute focus:fill-region-artifact-mid/50 hover:fill-region-artifact-mid/50":
                  regionType === "artifact",
                "animate-table-attribute focus:fill-region-table-mid/50 hover:fill-region-table-mid/50":
                  regionType === "table",
                "animate-toc-attribute focus:fill-region-toc-mid/50 hover:fill-region-toc-mid/50":
                  regionType === "toc",
                "animate-text-attribute focus:fill-region-text-mid/50 hover:fill-region-text-mid/50":
                  regionType === "text",
              },
              className,
            )}
          />
        </Slottable>
      </svg>
    </Slot>
  );
}
